import * as React from 'react';
import { FormGroup, Input } from 'reactstrap';
import ValidationMessages from './ValidationMessages';
import { FieldRenderProps, useForm } from 'react-final-form';
import FieldLabel from './FieldLabel';
import { DropdownProps, PrefixSuffixFieldProps } from 'components/shared/edit/fields';
import { toKebabCase } from 'util/StringUtil';
import { useEffect, useState } from 'react';

type FormDropdownProps = FieldRenderProps<any, HTMLInputElement> &
	DropdownProps &
	PrefixSuffixFieldProps & {
		/**
		 * If there's only a single value to pick from, and no value is currently selected, pick that automatically.
		 */
		lockSingleValueSelection?: boolean;
	};

/**
 * Component that renders a dropdown. Utilizes reactstrap Input and final form.
 *
 * @class FormDropdown
 * @extends {(React.Component<FieldRenderProps & DropdownProps>)}
 */
function FormDropdown(props: FormDropdownProps) {
	const form = useForm();

	const {
		input: { name, onChange, value, onBlur, onFocus },
		meta,
		disabled,
		readOnly,
		addSelectOption,
		options,
		indicateDirty,
		prefix,
		suffix,
		innerRef,
		lockSingleValueSelection = false,
		multiple,
		testId,
	} = props;

	const [singleValueLocked, setSingleValueLocked] = useState(
		value && lockSingleValueSelection && options.length === 1,
	);

	useEffect(() => {
		if (lockSingleValueSelection && options.length === 1) {
			// console.debug('Single value detected for', name, '- auto selecting.');
			form.change(name, options[0].value);
			setSingleValueLocked(true);
		} else if (options.length !== 1) {
			setSingleValueLocked(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options]);

	return (
		<FormGroup className={indicateDirty && meta.dirty ? 'form-group--dirty' : ''}>
			<FieldLabel {...props} htmlFor={name} />

			<div className="input-wrapper">
				{prefix ? <div className="input-wrapper__prefix">{prefix}</div> : null}
				<Input
					id={name}
					invalid={!meta.dirtySinceLastSubmit && meta.submitError ? true : false}
					type="select"
					disabled={disabled}
					readOnly={readOnly || singleValueLocked}
					value={value}
					name={name}
					onChange={onChange}
					onBlur={onBlur}
					onFocus={onFocus}
					placeholder="Select..."
					data-testid={testId || `field-${toKebabCase(name)}`}
					innerRef={innerRef}
					multiple={multiple}
				>
					{addSelectOption ? <option value="" /> : null}
					{options.map((option, index) => (
						<option key={index} value={option.value} disabled={readOnly}>
							{option.text}
						</option>
					))}
				</Input>

				{suffix ? <div className="input-wrapper__suffix">{suffix}</div> : null}
			</div>

			<ValidationMessages
				id={name + '-invalid'}
				meta={meta}
				dataId={testId ? testId + '-validation-msg' : undefined}
			/>
		</FormGroup>
	);
}

export default FormDropdown;
